import { PRECISION } from './constants';
import { Part, SVG_NS, ViewContext } from './part';
import { getState } from './state';

export type HoleLocation =
  | 'rim'
  | 'rim-ds'
  | 'rim-nds'
  | 'flange-ds'
  | 'flange-nds'
  | 'nipple'
  | 'axle';

export interface HoleProps {
  angle: number;
  distance: number;
  diameter: number;
  location: HoleLocation;
  selectable?: boolean;
  centerOffset?: number;
}

export class Hole extends Part {
  children: Part[] = [];
  location: HoleLocation;
  private selectable: boolean;

  constructor(
    public id: string,
    public props: HoleProps
  ) {
    super();
    this.location = props.location;
    this.selectable = props.selectable ?? true;
  }

  update(props: Partial<HoleProps>) {
    this.props = {
      ...this.props,
      ...props,
    };
    this.location = this.props.location;
  }

  get xyMm(): [number, number] {
    const a = (Math.PI / 180) * (this.props.angle - 90);
    return [Math.cos(a) * this.props.distance, Math.sin(a) * this.props.distance];
  }

  get centerOffset(): number {
    return this.props.centerOffset || 0;
  }

  xy(viewCtx: ViewContext): [number, number] {
    const dist = viewCtx.schematicView
      ? this.schematicDist(viewCtx)
      : this.props.distance * viewCtx.scaler.pxPerMm;
    const a = (Math.PI / 180) * (this.props.angle - 90);
    const correction = viewCtx.viewSide === 'nds' ? -1 : 1;
    return [Math.cos(a) * dist * correction, Math.sin(a) * dist];
  }

  d(viewCtx: ViewContext): number {
    return viewCtx.schematicView
      ? this.schematicDiameter(viewCtx)
      : this.props.diameter * viewCtx.scaler.pxPerMm;
  }

  get isOnRim(): boolean {
    return this.location.split('-')[0] === 'rim';
  }

  get isOnFlange(): boolean {
    return this.location.split('-')[0] === 'flange';
  }

  schematicDiameter(viewCtx: ViewContext): number {
    const scaler = viewCtx.scaler;
    switch (this.props.location) {
      case 'rim':
        return scaler.schematicRimHoleDiameter;
      case 'rim-ds':
        return viewCtx.viewSide === 'ds'
          ? scaler.schematicNearRimHoleDiameter
          : scaler.schematicFarRimHoleDiameter;
      case 'rim-nds':
        return viewCtx.viewSide === 'nds'
          ? scaler.schematicNearRimHoleDiameter
          : scaler.schematicFarRimHoleDiameter;
      case 'flange-ds':
        return viewCtx.viewSide === 'ds'
          ? scaler.schematicNearFlangeHoleDiameter
          : scaler.schematicFarFlangeHoleDiameter;
      case 'flange-nds':
        return viewCtx.viewSide === 'nds'
          ? scaler.schematicNearFlangeHoleDiameter
          : scaler.schematicFarFlangeHoleDiameter;
      case 'nipple':
        return scaler.schematicNippleHoleDiameter;
      case 'axle':
        return scaler.schematicAxleDiameter;
      default:
        return 0;
    }
  }

  private schematicDist(viewCtx: ViewContext): number {
    const scaler = viewCtx.scaler;
    let d = 0;
    switch (this.props.location) {
      case 'rim':
      case 'rim-ds':
      case 'rim-nds':
      case 'nipple':
        d = (scaler.schematicRimInnerDiameter + scaler.schematicRimOuterDiameter) / 2;
        break;
      case 'flange-ds':
        d =
          viewCtx.viewSide === 'ds'
            ? scaler.schematicNearFlangeDiameter
            : scaler.schematicFarFlangeDiameter;
        break;
      case 'flange-nds':
        d =
          viewCtx.viewSide === 'nds'
            ? scaler.schematicNearFlangeDiameter
            : scaler.schematicFarFlangeDiameter;
        break;
      default:
        break;
    }
    return d / 2;
  }

  isOnFarSide(viewSide: string): boolean {
    const locationSide = this.location.split('-')[1];
    return !!locationSide && locationSide !== viewSide;
  }

  get isOnDs(): boolean {
    const locationSide = this.location.split('-')[1];
    return !locationSide || locationSide === 'ds';
  }

  render(viewCtx: ViewContext): SVGElement {
    const isSelected = getState().selectedId === this.id;
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('id', this.id);
    const title = document.createElementNS(SVG_NS, 'title');
    title.textContent = `Hole ${this.id}`;
    circle.appendChild(title);
    circle.classList.add('hole');
    isSelected && circle.classList.add('selected');
    this.isOnFarSide(viewCtx.viewSide) && circle.classList.add('far-side');
    !this.selectable && circle.classList.add('select-resetting');
    const [x, y] = this.xy(viewCtx);
    circle.setAttribute('cx', x.toFixed(PRECISION));
    circle.setAttribute('cy', y.toFixed(PRECISION));
    circle.setAttribute('r', (this.d(viewCtx) / 2).toFixed(PRECISION));
    this.addEventHandlers(circle);
    return circle;
  }

  addEventHandlers(elem: SVGElement): void {
    if (!this.selectable) return;
    elem.addEventListener('click', () => {
      const state = getState();
      if (state.selectedId === this.id) {
        state.select(undefined);
      } else if (state.selectedId === undefined) {
        state.select(this.id);
      } else if (!(state.wheel?.find(state.selectedId) instanceof Hole)) {
        state.select(this.id);
      } else if (state.selectedId !== undefined && state.selectedId !== this.id) {
        const otherHole = state.wheel?.find(state.selectedId);
        if (otherHole) {
          const added = state.wheel?.addSpoke(this, otherHole as Hole);
          state.refresh();
          if (!added) {
            state.select(this.id);
          }
        }
      }
    });
  }
}
